import moment from 'moment';
import {toastr} from 'react-redux-toastr';
import {getJsonAsync, postJsonAsync, putJsonAsync, navigateTo} from 'shared/actions';
import {initialize, change, reset, formValueSelector} from 'redux-form';
import {validateEmail} from 'shared/validators';
import { setErrorList } from 'shared/error/actionTypes';

import { displayErrorList } from '../../shared/error/errorManager';

import {
  rostersHasUserIdModifications
  } from 'shared/actionRosterPosition';
import {
  setEntryStateDisabled,
  setEntryReadOnlyInfo,
  setEntrySummary,
  clearRosterPosition,
  setVirtualEntriesToModify,
  setStates,
  setCountries,
  updateRosterInfo,
  searchingFinished,
  searchingStarts,
  setMemberSearchField,
  moveRosterUp,
  moveRosterDown,
  setRosterPositions,
  displayEntryModal,
  setEntryDetailBalance,
  setEntryDetailCaptainId,
  setPaymentLogHistory,
  displayPaymentLogHistory,
  displayEntryDetailPaymentModal,
  hideEntryDetailPaymentModal,
  cancelEntryDetailPaymentModal,
  setEntryDetailId,
  setEntryDetailTournament,
  displaySquadChangeModal,
  displayEventChangeModal,
  setEntryDetailCoordinator,
  setEntryDetailCaptain,
  displayEntryDetailCoordinatorModal,
  cancelEntryDetailCoordinatorModal,
  hideEntryDetailCoordinatorModal,
  displayEntryDetailChangeCaptainModal,
  cancelEntryDetailChangeCaptainModal,
  hideEntryDetailChangeCaptainModal,
  setIsNewCaptainOnSearch,
  setAvailableBowlersInState,
  setVisibleEntryDetailRefundModal, 
  setCreditPurchaseTransactions,
  displayReactivationModal,
  setAdminEntryDetailRefundType
} from './actionTypes';

export const handleCountryChange = value => (dispatch, getState) => {
  const countries = getState().admin.entryDetail.countries,
    selectedCountry = countries.find(x => x.value === value),
    disableEntryState = !selectedCountry || !selectedCountry.label || selectedCountry.label !== 'United States';

  dispatch(setEntryStateDisabled(disableEntryState));
  if(disableEntryState) {
    dispatch(change('adminEntryRosterForm', 'state', null));
  }
};

export const handleSaveEntryRosterForm = data => (dispatch, getState) => new Promise((resolve) => {
  const {entryId, rosterPositions} = getState().admin.entryDetail;

  // Make sure we don't save if there are modifications in the user ID 
  let hasRosterPositionChanged = rostersHasUserIdModifications(rosterPositions);

  // For some reason, we need to do this to refresh the layout 
  dispatch(setRosterPositions(rosterPositions.map(r => r)));
  if (hasRosterPositionChanged) {
    resolve();
    return;
  }

  // create payload using form data and roster positions from state
  const payload = {
    rosterPositions,
    name: data.name,
    state: data.state,
    countryId: data.countryId,
    pairCode: data.pairCode,
    groupCode: data.groupCode
  };

  dispatch(putJsonAsync(`admin/entry/${entryId}/roster`, payload, false))
  .then(() => {
    dispatch(reset('adminEntryRosterForm'));
    dispatch(displayEntryModal(false));
    toastr.success('Roster Updated!', 'Entry roster successfully saved!');
    dispatch(retrieveEntryDetails(entryId));
    resolve();
  })
  .catch(error => {
    
    if(!Array.isArray(error))
    {
      error = [ error.message ];
    }

    dispatch(displayErrorList(error));

    resolve();
  });
});

export const handleRosterPositionCleared = index => dispatch => {
  dispatch(clearRosterPosition(index));
  dispatch(change('adminEntryRosterForm', `average-${index + 1}`, ""));
  dispatch(autoSetTeamNameForRosterFromState());
  
  dispatch(retrieveRosterWarnings());
};

export const retrieveCancelEntryWarnings = () => (dispatch, getState) => new Promise((resolve) => {
  const {entryId} = getState().admin.entryDetail;

  dispatch(postJsonAsync(`admin/entry/${entryId}/cancelWarnings`))
    .then(response => {
      dispatch(setVirtualEntriesToModify(response.virtualEntriesToModify));
      resolve();
    })
    .catch(() => resolve()); 
});

export const retrieveRosterWarnings = () => (dispatch, getState) => new Promise((resolve) => {
  const {entryId, rosterPositions} = getState().admin.entryDetail;

  // create payload using form data and roster positions from state
  const payload = {
    rosterPositions
  };

  dispatch(setVirtualEntriesToModify(undefined));
  dispatch(postJsonAsync(`admin/entry/${entryId}/rosterWarnings`, payload))
    .then(response => {
      dispatch(setVirtualEntriesToModify(response.virtualEntriesToModify));
      resolve();
    })
    .catch(() => resolve()); 
});

export const retrieveStates = () => (dispatch, getState) => new Promise((resolve, reject) => {
  const states = getState().admin.entryDetail.states;

  // prevent going to server if countries already in state
  if(states && states.length) {
    resolve(states);
  }

  dispatch(getJsonAsync(`states`))
    .then(states => {
      dispatch(setStates(states));
      resolve(states);
    })
    .catch(() => reject());
});

export const retrieveCountries = () => (dispatch, getState) => new Promise((resolve, reject) => {
  const countries = getState().admin.entryDetail.countries;

  // prevent going to server if countries already in state
  if(countries && countries.length) {
    resolve(countries);
  }

  dispatch(getJsonAsync(`countries`))
  .then(countries => {
    dispatch(setCountries(countries.map(({id, name}) => ({
      value: id,
      label: name
    }))));
    resolve(countries);
  })
  .catch(() => reject());
});

export const validateEntryRosterForm = data => {
  const errors = {};

  if (!data.name) {
    errors.name = 'We need to be able to identify your team.';
  }
  return errors;
};

export const handleSearchChange = (roster, event) => dispatch => {
  roster._searchValue = event.target.value.trim();
  dispatch(setMemberSearchField(roster));
};

export const handleSearch = rosterPosition => (dispatch, getState) => {
  const tournamentId = getState().admin.entryDetail.tournament.id;
  const eventId = getState().admin.entryDetail.eventId; 
  const entryId = getState().admin.entryDetail.entryId; 

  // do nothing if usbc search field is empty
  if (!rosterPosition._searchValue) {
    return false;
  }

  dispatch(searchingStarts(rosterPosition));
  dispatch(getJsonAsync(`admin/tournament/${tournamentId}/member/${rosterPosition._searchValue}/event/${eventId}/entry/${entryId}`))
  .then(bowler => {
    const updatedRosterPosition = {
        ...rosterPosition,
        bowler: bowler.error ? {} : bowler,
        usbcId: rosterPosition._searchValue,
        usbcIdError: false,
        _errorMessage: bowler.error ? bowler.error.Message : null
      };

    dispatch(updateRosterInfo(updatedRosterPosition));
    dispatch(setRosterPositionAverage(updatedRosterPosition));
    dispatch(autoSetTeamNameForRosterFromState());
    dispatch(searchingFinished());
    
    dispatch(retrieveRosterWarnings());
  })
  .catch(() => dispatch(searchingFinished()));
};

export const setRosterPositionAverage = rosterPosition => dispatch =>
  dispatch(change('adminEntryRosterForm', `average-${rosterPosition.position}`, rosterPosition.bowler.average));

export const handleSearchEnterKeyPress = (roster, event) => dispatch => {
  if(event.keyCode === 13) {
    event.preventDefault();
    event.stopPropagation();
    dispatch(handleSearch(roster));
  }
};

export const autoSetTeamNameForRosterFromState = () => (dispatch, getState) => {
  const state = getState(),
    rosterPositions = state.admin.entryDetail.rosterPositions,
    rosterPositionsQty = rosterPositions.length;

  // singles
  if(rosterPositionsQty === 1) {
    dispatch(change('adminEntryRosterForm', `name`, `${rosterPositions[0].bowler.firstName} ${rosterPositions[0].bowler.lastName}`));

  // doubles
  } else if(rosterPositionsQty === 2) {
    const teamNameA = rosterPositions[0].bowler && rosterPositions[0].bowler.lastName
      ? rosterPositions[0].bowler.lastName
      : '';
    const teamNameB = rosterPositions[1].bowler && rosterPositions[1].bowler.lastName
      ? rosterPositions[1].bowler.lastName
      : '';

    if(teamNameA || teamNameB) {
      dispatch(change('adminEntryRosterForm', `name`, `${teamNameA}${teamNameA && teamNameB ? '/': ''}${teamNameB}`));
    }
  }
};

export const handleMoveDownClick = index => (dispatch, getState) => {
  const formValues = getState().form.adminEntryRosterForm.values;

  dispatch(moveRosterDown(index));

  // swap averages in form data because of redux-form is handling those fields
  dispatch(change('adminEntryRosterForm', `average-${index + 1}`, formValues[`average-${index + 2}`]));
  dispatch(change('adminEntryRosterForm', `average-${index + 2}`, formValues[`average-${index + 1}`]));
  dispatch(autoSetTeamNameForRosterFromState());
};

export const handleMoveUpClick = index => (dispatch, getState) => {
  const formValues = getState().form.adminEntryRosterForm.values;

  dispatch(moveRosterUp(index));

  // swap averages in form data because of redux-form is handling those fields
  dispatch(change('adminEntryRosterForm', `average-${index + 1}`, formValues[`average-${index}`]));
  dispatch(change('adminEntryRosterForm', `average-${index}`, formValues[`average-${index + 1}`]));
  dispatch(autoSetTeamNameForRosterFromState());
};

export const handleCancelClick = () => dispatch => {
  dispatch(displayEntryModal(false));
  dispatch(reset('adminEntryRosterForm'));
};

export const handleViewRosterClick = () => (dispatch, getState) => {
  const entryId = getState().admin.entryDetail.entryId;

  dispatch(getJsonAsync(`admin/entry/${entryId}/readonly`))
  .then(readOnlyInfo => {
    dispatch(displayEntryModal(true));
    dispatch(setEntryReadOnlyInfo(readOnlyInfo));
  });
};

export const handleAdminManageRosterClick = () => (dispatch, getState) => {
  dispatch(retrieveStates())
  .then(() => {
    dispatch(retrieveCountries())
    .then(() => {
      const {entryId, countries} = getState().admin.entryDetail;
 
      dispatch(setVirtualEntriesToModify(undefined));
      dispatch(getJsonAsync(`admin/entry/${entryId}/roster`))
      .then(entry => {
        const selectedCountry = countries.find(x => x.value === entry.countryId),
          disableEntryState = !selectedCountry || !selectedCountry.label || selectedCountry.label !== 'United States';
        let rosterPositionsAverage = {};
        const rosterPositions = entry.rosterPositions.map(roster => ({
          ...roster,
          ...{
            _searchValue: roster.bowler.usbcId || '',
            _processing: false,
            _retrieved: !!roster.bowler.usbcId && !roster.bowler.error,
            _errorMessage: roster.bowler.error ? roster.bowler.error.Message : null,
            bowler: roster.bowler.error ? {} : {
              ...roster.bowler,
              average: roster.bowler.average || ''
            }
          }
        }));

        const errorUsbcIds = rosterPositions.filter(rosterPosition => rosterPosition._errorMessage).map(rosterPosition => rosterPosition._searchValue);

        if (errorUsbcIds && errorUsbcIds.length > 0) {
          toastr.error(`USBC ID${errorUsbcIds.length === 1 ? '' : 's'} ${errorUsbcIds.join()} ${errorUsbcIds.length === 1 ? 'is' : 'are'} not found in membership`, '', { allowHtml: true });
        }
        
        dispatch(displayEntryModal(true));
        dispatch(setRosterPositions(rosterPositions));

        // generates redux-form model for roster positions averages
        rosterPositions.forEach(roster =>
          rosterPositionsAverage[`average-${roster.position}`] = roster.bowler.average);

        // re-initialize redux-form with entry data
        dispatch(initialize('adminEntryRosterForm', {
          ...rosterPositionsAverage,
          ...entry,
          rosterPositionsQty: rosterPositions.length
        }));
        dispatch(setEntryStateDisabled(disableEntryState));
    
        // initialize available bowler list for doubles/singles dropwdown
        dispatch(retrieveAvailableBowlers(entryId));
      });
    });
  });
};

export const retrieveAvailableBowlers = entryId => dispatch => new Promise((resolve, reject) => {
  dispatch(getJsonAsync(`admin/entry/${entryId}/bowlers`))
  .then(bowlers => {
    dispatch(setAvailableBowlersInState(bowlers));
    resolve(bowlers);
  })
  .catch(() => reject());
});

export const retrieveEntryDetails = (entryId, paymentId) => dispatch => new Promise((resolve) => {
  dispatch(getJsonAsync(`admin/entry/${entryId}`))
  .then(response => { 
    if (response.parentEntryId){
      dispatch(navigateTo(`admin/entry/${response.parentEntryId}`));
      toastr.warning(`Showing the linked entry`, 
        `The entry ${response.entryNumber} is linked to this entry. You can only modify this entry.`);
      resolve(response);
      return;
    }
    dispatch(initializeDetailForms(entryId, response));
    dispatch(setEntryDetails(entryId, response));

    if (paymentId) {
      dispatch(entryPayment(entryId, paymentId));
    }
    resolve(response);
  });
});

const entryPayment = (entryId, paymentId) => dispatch => {
  dispatch(postJsonAsync(`admin/entry/${entryId}/payment/balance`, {
    BypassWebHooks: localStorage.bypassWebHooks,
    paymentId: paymentId
  }))
  .then(response => {        
    if (response) toastr.success('Payment Processed!', 'Selected entries were paid successfully.');
    dispatch(setEntryDetailBalance(response.balance));
    dispatch(reset('adminEntryPaymentForm'));  
  });

  if (localStorage.bypassWebHooks)
    delete localStorage.bypassWebHooks;
};

const initializeDetailForms = (entryId, response) => dispatch => {
  dispatch(initialize('adminEntryPaymentForm', {
    entryId: entryId
  }));

  dispatch(initialize('adminEntryDetailSquadChangeForm', {
    entryId: entryId,
    currentSquadDate: response.squadDateTime ? moment.parseZone(response.squadDateTime).format('YYYY-MM-DD') : null,
    currentSquadId: response.squadId,
    availableSquads: response.availableSquads.map(x => ({
      value: x.id,
      label: x.description
    }))
  }));

  if (response.availableEvents) {
    dispatch(initialize('adminEntryDetailEventChangeForm', {
      entryId: entryId,
      availableEvents: response.availableEvents.map(x => ({
        value: x.id,
        label: x.description
      }))
    }));
  }
};

const setEntryDetails = (entryId, response) => dispatch => {
  dispatch(setEntrySummary({
    entryNumber: response.entryNumber,
    groupCode: response.groupCode,
    pairCode: response.pairCode,
    readOnly: response.readOnly,
    isCancelled: response.isCancelled,
    teamName: response.teamName,
    divisionName: response.divisionName,
    timeZoneDisplayName: response.timeZoneDisplayName,
    squadDateTime: response.squadDateTime,
    eventId: response.eventId
  }));
  dispatch(setEntryDetailBalance(response.balance));
  dispatch(setEntryDetailCaptainId(response.captainId));
  dispatch(setEntryDetailId(entryId));
  dispatch(setEntryDetailTournament({
    id: response.tournamentId,
    name: response.tournamentName,
    stripeKey: response.tournamentStripeKey,
    startDate: response.tournamentStartDate,
    endDate: response.tournamentEndDate,
    allowChangeBetweenFreeEvents: response.allowChangeBetweenFreeEvents
  }));
};

export const retrieveEntryCaptainDetails = (captainId) => (dispatch, getState) =>{
  const readOnly = getState().admin.entryDetail.readOnly;

  dispatch(postJsonAsync(`admin/captain/${captainId}`))
  .then(response => {
    dispatch(setEntryDetailCaptain({
      captainId: captainId,
      captainFirstName: response.captainFirstName,
      captainLastName: response.captainLastName,
      captainEmail: response.captainEmail,
      readOnly
    }));
  });
};

export const retrieveEntryCoordinatorDetails = (coordinatorId) => dispatch =>{
  if(coordinatorId === undefined || coordinatorId === null) {
    dispatch(setEntryDetailCoordinator({
      firstName: '',
      lastName: '',
      email: ''
    }));
    return;
  }
  dispatch(getJsonAsync(`admin/coordinator/${coordinatorId}`))
  .then(response => {
    dispatch(setEntryDetailCoordinator({
      firstName: response.firstName,
      lastName: response.lastName,
      email: response.email
    }));
  });
};

export const handlePaymentLogClick = () => (dispatch, getState) => {
  const {entryId, showPaymentLog} = getState().admin.entryDetail;

  if(showPaymentLog) {
    dispatch(displayPaymentLogHistory(false));
    return;
  }

  dispatch(retrievePaymentLog(entryId))
  .then(() => dispatch(displayPaymentLogHistory()));
};

export const retrievePaymentLog = entryId => dispatch => new Promise(resolve => {
  dispatch(getJsonAsync(`admin/entry/${entryId}/payment/log`))
  .then(paymentLog => {
    dispatch(setPaymentLogHistory(paymentLog));
    resolve(paymentLog);
  })
  .catch(() => resolve());
});

export const validateEntryDetailSquadChangeForm = data => {
  let errors = {};

  if (!data.currentSquadId) {
    errors.currentSquadId = 'There are no squads available on this date';
  }

  return errors;
};

export const validateEntryCaptainChangeForm = data => {
  const errors = {};

  if(!data.captainEmail || !validateEmail(data.captainEmail)) {
    errors.captainEmail = 'Please provide a valid email.';
  }

  if (!data.captainFirstName) {
    errors.captainFirstName = 'Please search for the captain before updating.';
  }

  return errors;
};

export const validateEntryCoordinatorDetailForm = data => {
  const errors = {};

  if(!data.email || !validateEmail(data.email)) {
    errors.email = 'Please provide a valid email.';
  }

  return errors;
};

export const handleEntryCaptainChangeFormSave = data => (dispatch, getState) => new Promise(() => {
  const entryId = getState().admin.entryDetail.entryId;
  const virtualEntries = getState().admin.captainDetail.affectedVirtualEntries
  .filter(entry => entry.selected)
  .map(entry => entry.virtualEntryId);

  const request = {
    entryId: entryId, 
    firstName: data.captainFirstName,
    lastName: data.captainLastName,
    email: data.captainEmail,
    virtualEntryIds: virtualEntries
  };

  dispatch(changeEntryCaptain(request));
});

export const changeEntryCaptain = request => (dispatch) => new Promise((resolve) => {
  dispatch(postJsonAsync(`admin/captain/change/entry`, request))
  .then(response => {
    dispatch(hideEntryDetailChangeCaptainModal());
    toastr.success('Captain Changed!', 'Captain was changed.');
    dispatch(setEntryDetailCaptainId(response.captainId));
    dispatch(setEntryDetailCaptain({
      captainId: response.captainId,
      captainFirstName: response.firstName,
      captainLastName: response.lastName,
      captainEmail: response.email
    }));
    resolve();
  })
  .catch(() => {
    toastr.error('Captain Not Changed!', 'There was an error changing the captain.');
    resolve();
  });
});

export const handleEntryDetailPaymentClick = () => dispatch => {
  dispatch(displayEntryDetailPaymentModal());
};

export const validate = (data) => {
  const errors = {};

  if (!data.name) {
    errors.name = 'Please provide a name for this account.';
  }
  return errors;
};

export const handleEntryDetailCreditCardPaymentClick = () => (dispatch, getState) => {
  const state = getState();
  const entryId = state.admin.entryDetail.entryId;
  const tournament = state.admin.entryDetail.tournament;
  let body = {
    entryId: entryId
  };

  dispatch(postJsonAsync(`admin/entry/createSession`, body))
  .then(response => {
    const publishableKey = tournament.stripeKey;
    const stripe = window.Stripe(publishableKey);
    const sessionId = response.paymentId;

    stripe.redirectToCheckout({
      sessionId
    });
  });
};

export const handleEntryDetailPaymentCancelClick = () => dispatch => {
  dispatch(cancelEntryDetailPaymentModal());
  dispatch(reset('adminEntryPaymentForm'));
  dispatch(setErrorList([]));
};

export const handlePaymentSave = data => (dispatch, getState) => new Promise((resolve) => {
  const showPaymentLog = getState().admin.entryDetail.showPaymentLog;
  
  if (data.stripeTransactionId) data.stripeTransactionId = data.stripeTransactionId.trim();

  dispatch(postJsonAsync(`admin/entry/${data.entryId}/payment/other`, data))
  .then(response => {
    toastr.success('Saved!', 'Payment was successfully saved.');
    dispatch(setEntryDetailBalance(response.balance));
    dispatch(hideEntryDetailPaymentModal());
    dispatch(reset('adminEntryPaymentForm'));

    // update payment log info if container is visible
    if(showPaymentLog) {
      dispatch(retrievePaymentLog(data.entryId))
      .then(() => resolve());
    } else {
      resolve();
    }
  })
  .catch(() => resolve());
});

export const handleSquadChangeClick = () => dispatch => {
  dispatch(displaySquadChangeModal(true));
};

export const handleSquadChangeCancel = () => dispatch => {
  dispatch(displaySquadChangeModal(false));
};

export const handleSquadChangeSave = data => dispatch => new Promise((resolve, reject) => {
  const request = {
    entryId: data.entryId,
    squadId: data.currentSquadId
  };

  dispatch(putJsonAsync(`admin/entry/${request.entryId}/squad/change`, request))
  .then(() => {
    dispatch(displaySquadChangeModal(false));
    dispatch(retrieveEntryDetails(request.entryId));
    toastr.success('Squad Changed!', 'The squad was changed successfully.');
    resolve();
  })
  .catch(error => {
    toastr.error('Squad Not Updated!', 'There was an error changing squad for the entry.');
    reject(error);
  });
});

export const handleChangeSquadDateChanged = date => dispatch => {
  dispatch(change('adminEntryDetailSquadChangeForm', 'currentSquadDate', date));
  dispatch(updateAvailableSquadsForDate(date));
};

export const updateAvailableSquadsForDate = date => (dispatch, getState) => {
  const entryId = getState().admin.entryDetail.entryId,
    formattedDate = moment.parseZone(date).format(`YYYY-MM-DD`);

  dispatch(getJsonAsync(`admin/entry/${entryId}/squads/${formattedDate}`))
  .then(response => {
    const availableSquads = response.map(x => ({
      value: x.id,
      label: x.description
    }));

    dispatch(change('adminEntryDetailSquadChangeForm', 'availableSquads', availableSquads));
    dispatch(change('adminEntryDetailSquadChangeForm', 'currentSquadId', availableSquads.length ? availableSquads[0].value : '' ));

  })
  .catch(() => {
    toastr.error('Error getting squads!', 'There was an error getting available squad times.');
  });
};

export const handleEntryDetailCoordinatorUpdateClick = () => (dispatch, getState) => {
  const state = getState(),
  coordinator = state.admin.entryDetail.coordinator;

  dispatch(displayEntryDetailCoordinatorModal());
  dispatch(initialize('adminEntryCoordinatorDetailForm', coordinator));
};

export const handleEntryDetailChangeCaptainClick = () => (dispatch, getState) => {
  const state = getState(),
  captain = state.admin.entryDetail.captain;

  dispatch(displayEntryDetailChangeCaptainModal());
  dispatch(initialize('adminEntryCaptainChangeForm', captain));
};

export const handleEntryDetailCoordinatorUpdateCancelClick = () => dispatch => {
  dispatch(cancelEntryDetailCoordinatorModal());
  dispatch(reset('adminEntryCoordinatorDetailForm'));
  dispatch(setErrorList([]));
};

export const handleEntryChangeCaptainCancelClick = () => dispatch => {
  dispatch(cancelEntryDetailChangeCaptainModal());
  dispatch(reset('adminEntryCaptainChangeForm'));
  dispatch(setErrorList([]));
};

export const handleEntryChangeCaptainSearchClick = () => (dispatch, getState) => {
  const selector = formValueSelector('adminEntryCaptainChangeForm');
  const captainEmail = selector(getState(), 'captainEmail');

  dispatch(getJsonAsync(`admin/captain/${captainEmail}/email`))
  .then(response => {
    if(response){
      dispatch(change('adminEntryCaptainChangeForm', 'captainFirstName', response.captainFirstName));
      dispatch(change('adminEntryCaptainChangeForm', 'captainLastName', response.captainLastName));
      dispatch(setIsNewCaptainOnSearch(false));
    }
    else{
      dispatch(change('adminEntryCaptainChangeForm', 'captainFirstName', ''));
      dispatch(change('adminEntryCaptainChangeForm', 'captainLastName', ''));
      dispatch(setIsNewCaptainOnSearch(true));
    }
  })
  .catch(() => {
    toastr.error('Error getting captain detail!', 'There was an error getting captain detail.');
  });
};

export const handleCoordinatorSave = data => (dispatch, getState) => new Promise((resolve) => {
  const entryId = getState().admin.entryDetail.entryId;
  const request = {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email
  };

  dispatch(postJsonAsync(`admin/entry/${entryId}/coordinator`, request))
  .then(response => {
    dispatch(hideEntryDetailCoordinatorModal());
    toastr.success('Coordinator Updated!', 'Coordinator was updated.');
    dispatch(setEntryDetailCoordinator({
      firstName: response.firstName,
      lastName: response.lastName,
      email: response.email
    }));
    resolve();
  })
  .catch(() => {
    toastr.error('Coordinator Not Updated!', 'There was an error updating the coordinator.');
    resolve();
  });
});

export const handleRemoveSquadClick = () => (dispatch,getState) => new Promise((resolve, reject) => {
  const request = {
    entryId: getState().admin.entryDetail.entryId,
    squadId: null
  };

  dispatch(putJsonAsync(`admin/entry/${request.entryId}/squad/change`, request))
  .then(() => {
    dispatch(retrieveEntryDetails(request.entryId));
    toastr.success('Squad Removed!', 'The entry was removed from the squad successfully.');
    resolve();
  })
  .catch(error => {
    toastr.error('Squad Not Removed!', 'There was an error removing the squad from the entry.');
    reject(error);
  });
});

export const handleEntryDetailCancelEntryClick = () => dispatch => {
  dispatch(setAdminEntryDetailRefundType('refundWithCancellation'));
  dispatch(retrieveCancelEntryWarnings());
  dispatch(displayEntryDetailRefundModal());
};

export const displayEntryDetailRefundModal = () => (dispatch, getState) => new Promise((resolve, reject) => {
  const {entryId } = getState().admin.entryDetail;
  
  dispatch(setVisibleEntryDetailRefundModal(true));
  dispatch(getJsonAsync(`admin/entry/${entryId}/payment/creditLog`)) 
  .then(response => {
    dispatch(setCreditPurchaseTransactions(response.payments));
    resolve();
  })
  .catch(error => reject(error));
});

export const handleEntryDetailRefundCancelClick = () => dispatch => {
  dispatch(setVisibleEntryDetailRefundModal(false));
  dispatch(reset('adminEntryRefundForm'));
  dispatch(setErrorList([]));
};

export const handleEntryDetailRefundSubmitClick = data => (dispatch, getState) => new Promise((resolve) => {
  const state = getState().admin.entryDetail;
  const showPaymentLog = state.showPaymentLog;
  const entryId = state.entryId;
  const refundType = state.refundType;

  if (data.stripeTransactionId) data.stripeTransactionId = data.stripeTransactionId.trim();

  data = {
    ...data,
    refundOnly: refundType === 'refundOnly'
  };

  dispatch(postJsonAsync(`admin/entry/${entryId}/cancel`, data))
    .then(() => {
      if (refundType === 'refundOnly') {
        toastr.success('Saved!', 'The refund was successful.');
      } else {
        toastr.success('Saved!', 'The cancellation was successful.');
      }
      dispatch(retrieveEntryDetails(entryId));
      dispatch(setVisibleEntryDetailRefundModal(false));
      dispatch(reset('adminEntryRefundForm'));

      if (showPaymentLog) {
        dispatch(retrievePaymentLog(entryId))
          .then(() => resolve());
      } else {
        resolve();
      }
    })
    .catch(() => resolve());
});

export const handleEntryDetailReactivateEntryClick = () => dispatch => {
  dispatch(displayReactivationModal(true));
};

export const handleReactivationCancel = () => dispatch => {
  dispatch(displayReactivationModal(false));
};

export const handleReactivationChangeSquadDateChanged = date => dispatch => {
  dispatch(change('adminEntryDetailReactivationForm', 'currentSquadDate', date));
  dispatch(updateAvailableSquadsForReactivationDate(date));
};

export const updateAvailableSquadsForReactivationDate = date => (dispatch, getState) => {
  const entryId = getState().admin.entryDetail.entryId,
    formattedDate = moment.parseZone(date).format(`YYYY-MM-DD`);

  dispatch(getJsonAsync(`admin/entry/${entryId}/squads/${formattedDate}`))
  .then(response => {
    const availableSquads = response.map(x => ({
      value: x.id,
      label: x.description
    }));

    dispatch(change('adminEntryDetailReactivationForm', 'availableSquads', availableSquads));
    dispatch(change('adminEntryDetailReactivationForm', 'currentSquadId', availableSquads.length ? availableSquads[0].value : '' ));

  })
  .catch(() => {
    toastr.error('Error getting squads!', 'There was an error getting available squad times.');
  });
};

export const handleReactivationSave = data => (dispatch, getState) => new Promise((resolve, reject) => {
  const entryId = getState().admin.entryDetail.entryId;
  const request = {
    entryId: entryId,
    squadId: data.currentSquadId
  };

  dispatch(postJsonAsync(`admin/entry/${request.entryId}/reactivate`, request))
  .then(() => {
    dispatch(displayReactivationModal(false));
    dispatch(retrieveEntryDetails(request.entryId));
    toastr.success('Reactivation Saved!', 'The entry was successfully reactivated and added to the squad.');
    resolve();
  })
  .catch(error => {
    toastr.error('Reactivation not successful!', 'There was an error reactivating and updating the squad for the entry.');
    reject(error);
  });
});

export const handleEntryDetailRefundClick = () => dispatch => {
  dispatch(setAdminEntryDetailRefundType('refundOnly'));
  dispatch(displayEntryDetailRefundModal());
};

export const handleEventChangeClick = () => dispatch => {
  dispatch(displayEventChangeModal(true));
};

export const handleEventChangeCancel = () => dispatch => {
  dispatch(displayEventChangeModal(false));
};

export const handleEventChangeSave = data => dispatch => new Promise((resolve, reject) => {
  const request = {
    entryId: data.entryId,
    squadId: data.currentSquadId
  };

  // Note this is the same URL used by handleSquadChangeSave
  dispatch(putJsonAsync(`admin/entry/${request.entryId}/squad/change`, request))
  .then(() => {
    dispatch(displayEventChangeModal(false));
    dispatch(retrieveEntryDetails(request.entryId));
    toastr.success('Event Changed!', 'The event was changed successfully.');
    resolve();
  })
  .catch(error => {
    toastr.error('Event Not Updated!', 'There was an error changing event for the entry.');
    reject(error);
  });
});



// WEBPACK FOOTER //
// ./src/features/admin/entryDetail/actions.js